import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { ConfirmDialog, useConfirm } from '../components/common/ConfirmDialog';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { user, openLoginModal } = useAuth();
  const { state: confirmState, confirm, onResolve } = useConfirm();

  const fetchCart = useCallback(async () => {
    const user = JSON.parse(localStorage.getItem('user'));
    if (!user) {
      setCartItems([]);
      return;
    }

    // Modern UID approach: user_id is the primary key in "User" table
    // Prioritize user_id (UUID) for database operations
    const userId = user.u_id || user.user_id || user.id;
    if (!userId) {
      console.warn('CartContext: No valid user_id found in storage');
      setCartItems([]);
      return;
    }
    setIsLoading(true);
    try {
      const response = await fetch(`/api/cart/${userId}`);
      if (response.ok) {
        const data = await response.json();
        // Robust parsing of details and ensuring it's an array
        const items = (Array.isArray(data) ? data : []).map(item => ({
          ...item,
          details: typeof item.details === 'string' ? JSON.parse(item.details) : (item.details || {})
        }));
        setCartItems(items);
      }
    } catch (error) {
      console.error('Error fetching cart:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCart();

    // Listen for storage changes (login/logout)
    const handleStorage = () => fetchCart();
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [fetchCart]);

  const addToCart = async (product, quantity = 1, options = {}) => {
    if (!user) {
      openLoginModal();
      return;
    }

    const userId = user.u_id || user.user_id || user.id;
    const productId = product.p_id || product.id;

    if (!productId) {
      console.error('CartContext: Missing p_id (UUID) for product. Cannot add to cart.', product);
      return;
    }

    // Same product+variant = update quantity and tier; different variant = new entry
    const incomingVariant = typeof options.variant === 'object' ? (options.variant?.name || null) : (options.variant || null);
    const incomingTier = options.tier || null;
    const existingItem = cartItems.find(i => {
      const sameProduct = (i.product?.p_id || i.product?.id) === productId;
      const cartVariant = typeof i.details?.variant === 'object' ? (i.details?.variant?.name || null) : (i.details?.variant || null);
      return sameProduct && cartVariant === incomingVariant;
    });
    const stock = parseInt(product.quantity || 9999);
    // Total qty across ALL variants of this product
    const totalInCart = cartItems
      .filter(i => (i.product?.p_id || i.product?.id) === productId)
      .reduce((sum, i) => sum + parseInt(i.details?.quantity || i.quantity || 0), 0);
    if (existingItem) {
      const existingQty = parseInt(existingItem.details?.quantity || existingItem.quantity || 0);
      const remaining = stock - totalInCart;
      const requestedNewQty = existingQty + parseInt(quantity);
      const newQty = Math.min(requestedNewQty, existingQty + remaining);
      if (newQty < requestedNewQty && newQty > existingQty) {
        const confirmed = await confirm(
          `Only ${remaining} unit${remaining === 1 ? '' : 's'} left in stock. Want to add ${remaining} to your cart?`,
          'Limited Stock',
          remaining
        );
        if (!confirmed) { setIsCartOpen(true); return; }
      }
      if (newQty > existingQty) {
        try {
          const newDetails = { ...existingItem.details, quantity: newQty, tier: incomingTier, tierIndex: options.tierIndex ?? existingItem.details?.tierIndex ?? 0, price: options.price || existingItem.details?.price };
          const res = await fetch(`/api/cart/${existingItem.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ quantity: newQty, details: newDetails })
          });
          if (res.ok) {
            setCartItems(prev => prev.map(i =>
              i.id === existingItem.id ? { ...i, details: newDetails } : i
            ));
          }
        } catch (e) { console.error(e); }
      } else {
        alert(`Only ${stock} units available. You already have ${existingQty} in cart.`);
      }
      setIsCartOpen(true);
      return;
    }

    const remaining = stock - totalInCart;
    if (remaining <= 0) {
      alert(`Only ${stock} units available in total. Cart already has ${totalInCart}.`);
      setIsCartOpen(true);
      return;
    }
    const requestedQty = parseInt(quantity);
    const allowedQty = Math.min(requestedQty, remaining);
    if (allowedQty < requestedQty) {
      const confirmed = await confirm(
        `Only ${remaining} unit${remaining === 1 ? '' : 's'} left in stock. Want to add ${remaining} to your cart?`,
        'Limited Stock',
        remaining
      );
      if (!confirmed) return;
    }

    const details = {
      variant: options.variant || product.selectedVariant || null,
      tier: options.tier || null,
      tierIndex: options.tierIndex ?? 0,
      quantity: allowedQty,
      // Selling price = p_1 (Tier 1), matching what the product card/detail
      // display. main_p is the original/M.R.P (strike-through), so it must NOT
      // win over p_1 here or the cart would charge the higher display price.
      price: options.price || product.p_1 || product.main_p || "0"
    };

    console.log('CartContext: Adding to cart', { userId, productId, details });

    const postCart = () => fetch(`/api/cart`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, productId, details })
    });

    // The user can exist in Supabase Auth but not yet in the custom "User"
    // table that Cart.u_id references — create/sync it (idempotent upsert).
    const ensureUserRow = () => fetch(`/api/users/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        u_id: userId,
        email_id: user.email_id || user.email || '',
        name: user.name || (user.email_id || user.email || 'Customer').split('@')[0] || 'Customer',
        updated_at: new Date().toISOString()
      })
    }).catch(() => {});

    try {
      let response = await postCart();

      // Foreign-key violation → user row missing. Create it and retry once.
      if (!response.ok) {
        const errBody = await response.clone().json().catch(() => ({}));
        if (/foreign key|u_id_fkey|violates/i.test(errBody?.message || '')) {
          await ensureUserRow();
          response = await postCart();
        }
      }

      if (response.ok) {
        await fetchCart();
        setIsCartOpen(true);
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Server error' }));
        console.error('CartContext: Failed to add to cart', errorData);
        alert(`Could not add to cart: ${errorData.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('CartContext: Error adding to cart:', error);
      alert('Network error adding to cart. Please try again.');
    }
  };

  const removeFromCart = async (cartId) => {
    try {
      const response = await fetch(`/api/cart/${cartId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setCartItems(prev => prev.filter(item => item.id !== cartId));
      }
    } catch (error) {
      console.error('Error removing from cart:', error);
    }
  };

  const updateQuantity = async (cartId, newQuantity) => {
    const qty = parseInt(newQuantity);
    if (qty < 1) {
      return removeFromCart(cartId);
    }
    const item = cartItems.find(i => i.id === cartId);
    const stock = parseInt(item?.product?.quantity || 9999);
    const productId = item?.product?.p_id || item?.product?.id;
    // Total qty across all variants except current item
    const otherVariantsQty = cartItems
      .filter(i => (i.product?.p_id || i.product?.id) === productId && i.id !== cartId)
      .reduce((sum, i) => sum + parseInt(i.details?.quantity || i.quantity || 0), 0);
    const maxAllowed = stock - otherVariantsQty;
    const cappedQty = Math.min(qty, maxAllowed);
    if (cappedQty < 1) { alert(`Stock limit reached across all variants.`); return; }
    if (cappedQty !== qty) return;

    // Auto-select best tier label based on quantity
    const labelKeys = ['p1q','p2q','p3q','p4q','p5q','p6q','p7q','p8q','p9q','p10q'];
    let bestTierIdx = 0, bestTierLabel = null;
    labelKeys.forEach((key, i) => {
      const label = item?.product?.[key];
      if (!label) return;
      const minQty = parseInt((label || '').replace(/[^0-9]/g, '')) || 0;
      if (minQty > 0 && minQty <= qty) { bestTierIdx = i; bestTierLabel = label; }
    });
    const newDetails = { ...item?.details, quantity: qty, tierIndex: bestTierIdx, tier: bestTierLabel };

    try {
      const response = await fetch(`/api/cart/${cartId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quantity: qty, details: newDetails })
      });

      if (response.ok) {
        setCartItems(prev => prev.map(i =>
          i.id === cartId ? { ...i, details: newDetails } : i
        ));
      }
    } catch (error) {
      console.error('Error updating quantity:', error);
    }
  };

  // Empty the user's cart in the database AND in local state.
  // Called by CheckoutPage once an order has been successfully placed/paid.
  const clearCart = async () => {
    const stored = JSON.parse(localStorage.getItem('user') || 'null');
    const userId = stored?.u_id || stored?.user_id || stored?.id || user?.u_id || user?.id;
    // Optimistically clear UI even if the API call fails — the order is
    // already created server-side, we don't want to confuse the user.
    setCartItems([]);
    if (!userId) return;
    try {
      await fetch(`/api/cart/user/${userId}`, { method: 'DELETE' });
    } catch (error) {
      console.error('Error clearing cart:', error);
    }
  };

  const cartCount = cartItems.reduce((acc, item) => {
    const qty = parseInt(item.details?.quantity || item.qua || item.quantity || 1);
    return acc + qty;
  }, 0);

  const getTierPrice = (product, qty) => {
    const priceKeys = ['p_1','p_2','p_3','p_4','p_5','p_6','p_7','p_8','p_9','p_10'];
    const labelKeys = ['p1q','p2q','p3q','p4q','p5q','p6q','p7q','p8q','p9q','p10q'];
    let best = parseFloat(product?.p_1 || 0);
    for (let i = 0; i < 10; i++) {
      const price = parseFloat(product?.[priceKeys[i]] || 0);
      const minQty = parseInt((product?.[labelKeys[i]] || '').replace(/[^0-9]/g, '')) || (i === 0 ? 1 : 0);
      if (price > 0 && minQty > 0 && minQty <= qty) best = price;
    }
    return best;
  };

  const cartSubtotal = cartItems.reduce((acc, item) => {
    const qty = parseInt(item.details?.quantity || item.qua || item.quantity || 1);
    const price = getTierPrice(item.product, qty);
    return acc + (isNaN(price) ? 0 : price * qty);
  }, 0);

  return (
    <CartContext.Provider value={{
      cartItems,
      cartCount,
      cartSubtotal,
      isCartOpen,
      setIsCartOpen,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      isLoading,
      refreshCart: fetchCart
    }}>
      {children}
      <ConfirmDialog state={confirmState} onResolve={onResolve} />
    </CartContext.Provider>
  );
};
